import { computed, Directive, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { GuardsService } from '@services/guards/guards.service';
import { isArray, isString } from 'lodash';
import { catchError, forkJoin, from, map, Observable, of } from 'rxjs';

@Directive({
  selector: 'auth, [auth]',
  host: {
    '[class]': 'className()',
  },
})
export class AuthGuardDirective {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly guardsService = inject(GuardsService);
  private readonly router = inject(Router);
  private readonly routerLink = inject(RouterLink, { optional: true });

  readonly auth = input<string | string[]>();

  protected className = computed(() => (this.canShow.value() ? '' : 'd-none'));
  private canShow = rxResource({
    defaultValue: false,
    params: () => ({ auth: this.auth() }),
    stream: ({ params: { auth } }) => this.canShowCheck(auth),
  });

  private canShowCheck(auth?: string | string[]): Observable<boolean> {
    if (isArray(auth)) {
      const checks = auth.map(url => this.guardsService.canShow(this.getUrl(url)));
      return forkJoin(checks).pipe(
        map(results => results.includes(true)),
        catchError(() => of(false)),
      );
    }

    const url = isString(auth) ? auth : '';
    return from(this.guardsService.canShow(this.getUrl(url))).pipe(catchError(() => of(false)));
  }

  private getUrl(url = ''): string {
    if (url.length) {
      const urlTree = this.router.createUrlTree([url], { relativeTo: this.activatedRoute });
      url = this.router.serializeUrl(urlTree);
    } else if (this.routerLink?.urlTree) {
      url = this.router.serializeUrl(this.routerLink.urlTree);
    } else {
      console.error('Unsupported auth directive usage');
    }

    return url;
  }
}
